import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDownIcon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

export interface SingleSelectOption {
  id: string;
  label: string;
  sublabel?: string;
}

interface SingleSelectSearchableDropdownProps {
  options: SingleSelectOption[];
  selectedId: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  label?: string;
  isLoading?: boolean;
  emptyMessage?: string;
  allowClear?: boolean;
}

const SingleSelectSearchableDropdown: React.FC<SingleSelectSearchableDropdownProps> = ({
  options,
  selectedId,
  onChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  disabled = false,
  className = '',
  label,
  isLoading = false,
  emptyMessage = 'No options available',
  allowClear = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const lowerSearch = searchTerm.toLowerCase();
    return options.filter(
      option =>
        option.label.toLowerCase().includes(lowerSearch) ||
        (option.sublabel && option.sublabel.toLowerCase().includes(lowerSearch))
    );
  }, [options, searchTerm]);

  // Get selected label for display
  const selectedLabel = useMemo(() => {
    if (!selectedId) return null;
    const selected = options.find(option => option.id === selectedId);
    return selected?.label || null;
  }, [options, selectedId]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (id: string) => {
    onChange(id);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      {/* Trigger Button */}
      <button
        type="button"
        className={`relative w-full bg-white border rounded-md pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : ''
        }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        <span className="block truncate text-sm">
          {selectedLabel ? (
            selectedLabel
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-2">
          {allowClear && selectedId && !disabled && (
            <span
              className="h-5 w-5 text-gray-400 hover:text-gray-500 mr-1 cursor-pointer"
              onClick={handleClear}
            >
              <XMarkIcon className="h-5 w-5" />
            </span>
          )}
          <ChevronDownIcon
            className={`h-5 w-5 text-gray-400 transition-transform ${
              isOpen ? 'transform rotate-180' : ''
            }`}
          />
        </span>
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-72 rounded-md text-base ring-1 ring-black ring-opacity-5 overflow-hidden focus:outline-none">
          {/* Search Input */}
          <div className="px-3 py-2 border-b border-gray-200">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-52 overflow-auto">
            {isLoading ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                Loading...
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                {searchTerm ? 'No matches found' : emptyMessage}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selectedId === option.id;
                return (
                  <div
                    key={option.id}
                    className={`cursor-pointer select-none relative px-3 py-2 hover:bg-indigo-50 ${
                      isSelected ? 'bg-indigo-50' : ''
                    }`}
                    onClick={() => handleSelect(option.id)}
                  >
                    <div className="flex items-center">
                      <div>
                        <span className={`block text-sm ${isSelected ? 'font-medium text-indigo-900' : 'text-gray-900'}`}>
                          {option.label}
                        </span>
                        {option.sublabel && (
                          <span className="block text-xs text-gray-500">
                            {option.sublabel}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SingleSelectSearchableDropdown;
